import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router'
import useTitles from 'common/hooks/useTitles'
import ApplicationConf from 'conf/ApplicationConf'
import { ALIGN, CardTable, SORT } from 'common/components/datatable/Table'
import useFetch from 'common/hooks/useFetch'
import { getDate, getFullDateMini } from 'common/utils/DateUtil'
import PropTypes from 'prop-types'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent,
    Grid, Icon, Stack, Typography } from '@mui/material'
import Select from 'common/components/form/Select'
import { groupBy, isNil, keys, minBy, round, sortBy, sumBy, uniqBy } from 'lodash'
import { STATUS } from 'common/constants/storeConstants'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import Input from 'common/components/form/Input'
import useActions from 'common/hooks/useActions'
import { NEW, OTHER, REFRESH } from 'common/components/action/ActionConstant'
import { CardTitle } from 'common/components/card/Card'
import { darkBlue, mainBlue } from 'app/theme'
import IssuesAction from 'common/actions/IssuesAction'
import DevLotsAction from 'common/actions/DevLotsAction'
import { CATEGORIES_LABELS, HANDLERS_DEVS, STATUS_TICKETS, STATUS_TICKETS_ID_AND_LABEL, STATUS_TICKETS_LABEL } from 'common/constants/Constants'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { getColorAdv, getColorDueDate, PASTEL_COLOR } from 'common/utils/ColorUtil'
import DialogAddTicket from './DialogAddTicket'
import DialogUpdateLotName from './DialogUpdateLotName'
import DialogCreateLot from './DialogCreateLot'
import DialogLotDev from './DialogLotDev'
import DialogSyncLots from './DialogSyncLots'
import { getLotHealthColor, LOT_GROUP, SOURCE_AQUAPARC, SOURCE_MANTIS, tintColor, ticketsLabel, withDevLots, withMantisLots } from './DevLotsUtils'
import LotHeader from './LotHeader'
import DevStatsCard from './DevStatsCard'
import SuperMultiAutocomplete from 'common/components/form/SuperMultiAutocomplete'
import { getIconPriority, normalizeString } from 'common/utils/StringUtil'
import { fetchUsers, selectUsers } from 'admin/users/UsersSlice'

const headersCreatedTable = [{ key: 'avancement', value: '%' },
    { key: 'idIssue', value: 'Id' }, { key: 'summary', value: 'Titre' },
    { key: 'handler', value: 'Affecté à' }, { key: 'status', value: 'Statut' },
    { key: 'dueDate', value: 'Echéance' },
    { key: 'chiffrage', value: 'Chiffrage' }, { key: 'tempspasse', value: 'Temps passé' },
    { key: 'priority', value: 'Priorité' }, // { key: 'severity', value: 'Sévérité' },
    { key: 'category', value: 'Catégorie' },
    { key: 'reporter', value: 'Rapporteur' }, { key: 'project', value: 'Projet' },
    { key: 'lastUpdated', value: 'Date maj' },
]

// Largeurs fixes, donc colonnes alignées d'un lot à l'autre ; les colonnes optionnelles sont masquées
// quand AUCUN ticket affiché n'a de valeur (même jeu de colonnes pour tous les lots, pour que l'alignement tienne).
const COLUMN_WIDTHS = {
    avancement: 40, idIssue: 70, summary: 340, handler: 120, status: 210, dueDate: 110, chiffrage: 100,
    tempspasse: 110, priority: 130, category: 110, reporter: 120, project: 110, lastUpdated: 150,
}
const headersAligned = headersCreatedTable.map(h => ({ ...h, width: COLUMN_WIDTHS[h.key] }))
const OPTIONAL_COLUMN_FILLED = {
    dueDate: t => !!t.dueDate && t.dueDate !== 1,
    chiffrage: t => !!t.chiffrage,
    tempspasse: t => !!t.tempspasse,
}
const getVisibleHeaders = (tickets) => headersAligned.filter(h => isNil(OPTIONAL_COLUMN_FILLED[h.key]) || tickets.some(OPTIONAL_COLUMN_FILLED[h.key]))

// Force de la teinte d'en-tête d'un lot.
const HEADER_TINT_STRENGTH = 0.25

// Vue « Par projet » : on groupe déjà par projet → la colonne « Projet » devient « Lot ».
const headersByProject = headersCreatedTable.map(h => h.key === 'project' ? { key: 'lot', value: 'Lot' } : h)
// Vue « Par affectation » : on groupe déjà par affecté → la colonne « Affecté à » devient « Lot ».
const headersByHandler = headersCreatedTable.map(h => h.key === 'handler' ? { key: 'lot', value: 'Lot' } : h)

// Modes d'affichage du suivi des lots.
const VIEW_LOT = 'lot'
const VIEW_PROJECT = 'project'
const VIEW_AFFECTATION = 'affectation'
const VIEW_OPTIONS = [
    { id: VIEW_LOT, value: VIEW_LOT, label: 'Par type de lot' },
    { id: VIEW_PROJECT, value: VIEW_PROJECT, label: 'Par projet' },
    { id: VIEW_AFFECTATION, value: VIEW_AFFECTATION, label: 'Par affectation' },
]

// Source des lots, en attendant la bascule.
const SOURCE_OPTIONS = [
    { id: SOURCE_MANTIS, value: SOURCE_MANTIS, label: 'Lots Mantis' },
    { id: SOURCE_AQUAPARC, value: SOURCE_AQUAPARC, label: 'Lots Aquaparc' },
]

const progressBarSx = (value) => ({
    height: '20px',
    borderRadius: '5px',
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: 'white',
    },
    [`& .${linearProgressClasses.bar}`]: {
        backgroundColor: getColorAdv(value),
    },
})

// Ligne de tableau (lecture seule) pour un ticket. Partagée entre le tableau de lot
// (mode consultation) et les tableaux transverses des vues « Par projet » / « Par affectation ».
const formatTicketRow = (t) => {
    const avancement = t.avancement ?? 0
    return {
        id: t.id,
        lot: t.lot,
        idIssue: { value: t.id },
        summary: { value: t.summary?.length > 60 ? `${t.summary.substring(0, 60)}...` : t.summary, tooltip: t.summary?.length > 60 ? t.summary : undefined },
        category: { value: t.category },
        project: { value: t.project },
        reporter: { value: t.reporter },
        priority: { value: t.priority, leftIcon: getIconPriority(t.priority) },
        // severity: { value: t.severity },
        dueDate: {
            value: t.dueDate === 1 ? '' : getDate(t.dueDate * 1000),
            sortValue: t.dueDate,
            color: t.dueDate && t.dueDate !== 1 && ![STATUS_TICKETS_LABEL.ATTENTE_MISE_EN_PRODUCTION, STATUS_TICKETS_LABEL.EN_RECETTE, STATUS_TICKETS_LABEL.TRAITE, STATUS_TICKETS_LABEL.FERME].includes(t.status) ? getColorDueDate(t.dueDate * 1000) : '',
        },
        lastUpdated: { value: t.lastUpdated ? getFullDateMini(t.lastUpdated * 1000) : '', sortValue: t.lastUpdated },
        chiffrage: { value: t.chiffrage ? `${t.chiffrage}h` : '', sortValue: parseInt(t.chiffrage) },
        tempspasse: { value: t.tempspasse ? `${t.tempspasse}h` : '', sortValue: parseInt(t.tempspasse) },
        handler: { value: t.handler, sortValue: t.handler },
        status: {
            value: t.status,
            leftIcon: { icon: 'circle', color: STATUS_TICKETS.find(s => s.value === t.status)?.color },
            rightIcon: t.version === 'En recette' ? { icon: 'receipt_long', color: mainBlue } : {},
            sortValue: STATUS_TICKETS.find(s => s.value === t.status)?.sortValue,
        },
        avancement: {
            value: <b>{avancement}</b>,
            sortValue: parseInt(avancement),
            color: parseInt(avancement) !== 100 && [STATUS_TICKETS_LABEL.EN_RECETTE, STATUS_TICKETS_LABEL.TRAITE, STATUS_TICKETS_LABEL.FERME].includes(t.status) ? undefined : getColorAdv(avancement, t.status),
            align: ALIGN.RIGHT,
        },
    }
}

const TableLot = ({
    tickets = [],
    reload = () => {},
    dueDate,
    estimateTime = 0,
    spentTime = 0,
    lightMode,
    source = SOURCE_MANTIS,
    lotRef,
    allTickets = [],
    headers = headersAligned,
}) => {
    const [openAddDialog, setOpenAddDialog] = useState(false)
    const [openUpdateNameDialog, setOpenUpdateNameDialog] = useState(false)
    const [openLotDialog, setOpenLotDialog] = useState(false)
    const [editLot, setEditLot] = useState(false)
    const [ticketsChanges, setTicketsChanges] = useState([])
    const [ticketsToRemove, setTicketsToRemove] = useState([])
    const [loading, setLoading] = useState()

    const onChange = (id, obj) => {
        setTicketsChanges([
            ...ticketsChanges.filter(t => t.id !== id),
            {
                ...ticketsChanges.find(t => t.id === id),
                id,
                ...obj,
            },
        ])
    }

    const ticketsFormatted = useMemo(() => tickets.filter(t => !ticketsToRemove.includes(t.id)).map(t => {
        const base = formatTicketRow(t)
        if (!editLot) {
            return base
        }
        const {
            handler = t.handler,
            status = t.status,
            avancement = t.avancement ?? 0,
        } = ticketsChanges.find(c => c.id === t.id) ?? {}
        return {
            ...base,
            chiffrage: {
                value: <Input type='int' value={t.chiffrage} min={0} onChange={v => onChange(t.id, { chiffrage: v })} sx={{ maxWidth: '100px' }} />,
                sortValue: parseInt(t.chiffrage),
            },
            handler: {
                value: (
                    <Select
                        value={handler}
                        items={HANDLERS_DEVS}
                        onChange={v => onChange(t.id, { handler: v })}
                        sx={{ width: '100px' }}
                    />
                ),
                sortValue: handler,
            },
            status: {
                value: (
                    <Select
                        value={status}
                        items={STATUS_TICKETS.map(s => ({ ...s, label: s.value, id: s.value }))}
                        onChange={v => onChange(t.id, { status: v })}
                        sx={{ width: '100px' }}
                    />
                ),
                sortValue: STATUS_TICKETS.find(s => s.value === status)?.sortValue,
            },
            avancement: {
                ...base.avancement,
                value: <Input type='int' value={avancement} min={0} max={100} onChange={v => onChange(t.id, { avancement: v })} sx={{ maxWidth: '60px' }} />,
            },
        }
    }), [tickets, ticketsChanges, editLot, ticketsToRemove])

    const average = ticketsFormatted?.length ? Math.floor(sumBy(ticketsFormatted, t => parseInt(t.avancement.sortValue) || 0) / ticketsFormatted.length) : 0
    const lot = ticketsFormatted[0].lot
    const isAquaparc = source === SOURCE_AQUAPARC
    const readOnly = lightMode || (isAquaparc && isNil(lotRef))
    const healthColor = getLotHealthColor(average, dueDate)
    const tableHeaders = editLot ? headersAligned : headers

    const saveLot = (updatedLot) => DevLotsAction.update(updatedLot).then(response => {
        if (!isNil(response)) {
            setOpenLotDialog(false)
            reload()
        }
    })

    const actions = editLot ? [{
        icon: 'save',
        tooltip: 'Sauvegarder',
        onClick: () => {
            const removeFromLot = isAquaparc
                ? Promise.all(ticketsToRemove.map(id => DevLotsAction.unassign(id)))
                : IssuesAction.updateIssues(ticketsToRemove.map(id => ({ id, lot: '' })))
            removeFromLot.then(() => IssuesAction.updateIssues(ticketsChanges)).then(() => {
                reload()
                setTicketsChanges([])
            })
            setEditLot(false)
        },
    }, {
        icon: 'undo',
        tooltip: 'Annuler',
        onClick: () => {
            setTicketsChanges([])
            setTicketsToRemove([])
            setEditLot(false)
        },
    }] : [{
        icon: 'edit',
        tooltip: 'Modifier les tickets',
        onClick: () => setEditLot(true),
    }, {
        icon: 'add',
        tooltip: 'Ajouter des tickets',
        onClick: () => setOpenAddDialog(true),
    }]

    const lotAction = readOnly ? undefined : {
        icon: 'settings',
        tooltip: isAquaparc ? 'Modifier le lot' : 'Modifier le nom du lot',
        onClick: () => (isAquaparc ? setOpenLotDialog(true) : setOpenUpdateNameDialog(true)),
    }

    return (
        <>
            <Box
                sx={{
                    border: `1px solid ${tintColor(healthColor, 0.5)}`,
                    borderLeft: `6px solid ${healthColor}`,
                    borderRadius: '4px',
                    boxShadow: '0 2px 6px rgba(22, 24, 50, 0.2)',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                }}
            >
                <CardTable
                    key={lot}
                    title={(
                        <LotHeader
                            name={lot ?? 'Pas de lot'}
                            nbTickets={ticketsFormatted.length}
                            dueDate={dueDate}
                            spentTime={spentTime}
                            estimateTime={estimateTime}
                            average={average}
                            lotAction={lotAction}
                            actions={readOnly ? [] : actions}
                        />
                    )}
                    hideNbElements
                    hideLines={lightMode}
                    filterable={false}
                    lineActions={[{
                        icon: 'clear',
                        onClick: (t) => setTicketsToRemove((prevState) => ([...prevState, t.id])),
                        displayed: editLot,
                        tooltip: 'Retirer du lot',
                    }]}
                    rows={ticketsFormatted}
                    color={tintColor(healthColor, HEADER_TINT_STRENGTH)}
                    rowsPerPageDefault={25}
                    headers={tableHeaders}
                    onClickRow={({ id }) => !editLot && window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                    status={loading}
                    defaultSort={ { column: 'status', direction: SORT.ASC } }
                />
            </Box>
            <DialogAddTicket
                open={openAddDialog}
                lot={lot}
                tickets={allTickets}
                onClose={()=> setOpenAddDialog(false)}
                onAdd={(ids) => {
                    const added = isAquaparc
                        ? Promise.all(ids.map(id => DevLotsAction.assign(id, lotRef.id)))
                        : IssuesAction.updateIssues(ids.map(id => ({ id, lot })))
                    return added.then(reload)
                }}
            />
            <DialogUpdateLotName
                open={openUpdateNameDialog}
                lot={lot}
                onClose={()=> setOpenUpdateNameDialog(false)}
                onValidate={lotName => {
                    setLoading(STATUS.LOADING)
                    IssuesAction.updateIssues(tickets.map(t => ({ id: t.id, lot: lotName }))).then(() => {
                        setLoading()
                        reload()
                    })
                }}
            />
            {openLotDialog && <DialogLotDev lot={lotRef} onClose={() => setOpenLotDialog(false)} onValidate={saveLot} />}
        </>
    )
}

TableLot.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    reload: PropTypes.func,
    dueDate: PropTypes.number,
    estimateTime: PropTypes.number,
    spentTime: PropTypes.number,
    lightMode: PropTypes.bool,
    source: PropTypes.string,
    lotRef: PropTypes.shape({ id: PropTypes.number }),
    allTickets: PropTypes.arrayOf(PropTypes.shape({})),
    headers: PropTypes.arrayOf(PropTypes.shape({})),
}

const AccordionLots = ({
    title,
    tickets = [],
    allTickets = [],
    defaultExpanded,
    reload = () => {},
    lightMode,
    source,
    headers,
    accentColor = '#98a3b8',
}) => {
    const lots = groupBy(tickets, 'lot')
    const allLotsProps = keys(lots).map(name => {
        const { lotRef } = lots[name][0]
        const ticketsDueDate = minBy(lots[name].filter(t => t.dueDate !== 1 && ![STATUS_TICKETS_LABEL.EN_RECETTE, STATUS_TICKETS_LABEL.TRAITE, STATUS_TICKETS_LABEL.FERME].includes(t.status)), ['dueDate'])?.dueDate
        return {
            name,
            tickets: lots[name],
            lotRef,
            dueDate: ticketsDueDate ?? (isNil(lotRef?.echeance) ? undefined : Math.floor(lotRef.echeance / 1000)),
            estimateTime: round(lots[name].reduce((acc, t) => acc + Number(t.chiffrage) || 0, 0)),
            spentTime: round(lots[name].reduce((acc, t) => acc + Number(t.tempspasse) || 0, 0)),
        }
    })

    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary>
                <Stack direction='row' alignItems='center' spacing={1}>
                    <Icon fontSize='small' sx={{ color: accentColor }}>circle</Icon>
                    <span>{`${allLotsProps.length} ${title} (${ticketsLabel(tickets.length)})`}</span>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={3} direction='column'>
                    {sortBy(allLotsProps, ['dueDate', 'name']).map(l => <TableLot key={l.name} reload={reload} {...l} lightMode={lightMode} source={source} allTickets={allTickets} headers={headers} />)}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

AccordionLots.propTypes = {
    title: PropTypes.string,
    defaultExpanded: PropTypes.bool,
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    allTickets: PropTypes.arrayOf(PropTypes.shape({})),
    reload: PropTypes.func,
    lightMode: PropTypes.bool,
    source: PropTypes.string,
    headers: PropTypes.arrayOf(PropTypes.shape({})),
    accentColor: PropTypes.string,
}

// Tableau de tickets en lecture seule (pas d'édition de lot, ni d'ajout/renommage),
// avec un en-tête récapitulatif (temps passé / chiffré + avancement moyen).
// Utilisé par les vues transverses « Par projet » et « Par affectation ».
const TicketsTable = ({ title, tickets = [], color = darkBlue, headers = headersCreatedTable }) => {
    const rows = useMemo(() => tickets.map(formatTicketRow), [tickets])
    const average = rows.length ? Math.floor(sumBy(rows, r => parseInt(r.avancement.sortValue) || 0) / rows.length) : 0
    const estimateTime = round(tickets.reduce((acc, t) => acc + Number(t.chiffrage) || 0, 0))
    const spentTime = round(tickets.reduce((acc, t) => acc + Number(t.tempspasse) || 0, 0))

    return (
        <CardTable
            title={(
                <Grid container justifyContent='space-between' alignItems='center'>
                    <Typography variant='h6'>{`${title || 'Non renseigné'} (${rows.length} tickets)`}</Typography>
                    <Grid container sx={{ width: '450px' }} alignItems='center' justifyContent='space-between' spacing={2}>
                        <Grid size={4} container justifyContent='flex-end'>
                            <Typography variant='h6'>{`${spentTime}h / ${estimateTime}h`}</Typography>
                        </Grid>
                        <Grid size={6}>
                            <LinearProgress variant='determinate' value={average} sx={progressBarSx(average)} />
                        </Grid>
                        <Grid size={2}>
                            <Typography variant='h6'>{`${average}%`}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            hideNbElements
            rows={rows}
            color={color}
            rowsPerPageDefault={25}
            headers={headers}
            onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
            defaultSort={{ column: 'status', direction: SORT.ASC }}
        />
    )
}

TicketsTable.propTypes = {
    title: PropTypes.string,
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    color: PropTypes.string,
    headers: PropTypes.arrayOf(PropTypes.shape({})),
}

// Vue « Par projet » : un tableau par projet.
const ProjectsView = ({ tickets = [] }) => {
    const projects = groupBy(tickets, t => t.project || 'Sans projet')
    return (
        <Stack spacing={3} direction='column'>
            {sortBy(keys(projects)).map(name => (
                <TicketsTable key={name} title={name} tickets={projects[name]} headers={headersByProject} />
            ))}
        </Stack>
    )
}

ProjectsView.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
}

// Accordéon regroupant les tickets par affecté (un tableau par handler).
const AccordionByHandler = ({ title, tickets = [], defaultExpanded }) => {
    const handlers = groupBy(tickets, t => t.handler || 'Non affecté')
    const names = sortBy(keys(handlers))
    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary>{`${names.length} ${title} (${tickets.length} tickets)`}</AccordionSummary>
            <AccordionDetails>
                <Stack spacing={3} direction='column'>
                    {names.map(name => <TicketsTable key={name} title={name} tickets={handlers[name]} headers={headersByHandler} />)}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

AccordionByHandler.propTypes = {
    title: PropTypes.string,
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    defaultExpanded: PropTypes.bool,
}

// Affectations considérées internes même sans utilisateur Aquaparc associé
// (files d'attente d'équipe côté Mantis).
const EXTRA_INTERNAL_HANDLERS = ['Support N1', 'Support N2', 'Comite_Arbitrage']

// Vue « Par affectation » : un accordéon pour les affectés internes (dont le handler
// correspond au login Mantis d'un utilisateur Aquaparc, ou à une file d'équipe interne),
// un pour les externes.
const AffectationView = ({ tickets = [] }) => {
    const dispatch = useDispatch()
    const users = useSelector(selectUsers, shallowEqual)

    useEffect(() => {
        dispatch(fetchUsers())
    }, [dispatch])

    const internalLogins = [...users.map(u => u?.loginMantis).filter(Boolean), ...EXTRA_INTERNAL_HANDLERS]
    const internalTickets = tickets.filter(t => internalLogins.includes(t.handler))
    const externalTickets = tickets.filter(t => !internalLogins.includes(t.handler))

    return (
        <>
            <AccordionByHandler title='affectés internes' tickets={internalTickets} defaultExpanded />
            <AccordionByHandler title='affectés externes' tickets={externalTickets} />
        </>
    )
}

AffectationView.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
}

const FilterCard = ({
    tickets = [],
    filter = {},
    onChange = () => {},
    viewMode = VIEW_LOT,
    onChangeViewMode = () => {},
    source = SOURCE_MANTIS,
    onChangeSource = () => {},
}) => {
    const { searchValue = [], lot = [], status = [], category = [], reporter = [], handler = [], project = [] } = filter
    return (
        <Card sx={{ height: '100%' }}>
            <CardTitle title='Filtre' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                    <Grid size={4}>
                        <Input
                            label='Rechercher'
                            value={searchValue}
                            onChange={v => onChange({ ...filter, searchValue: v })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Lot'
                            values={lot}
                            options={sortBy(uniqBy(tickets, 'lot').map(t => ({ id: t.lot, value: t.lot, label: t.lot })), 'value')}
                            onChange={v => onChange({ ...filter, lot: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Statut'
                            values={status}
                            options={STATUS_TICKETS.map(s => ({ ...s, label: s.value }))}
                            onChange={v => onChange({ ...filter, status: v })}
                            undefinedValue
                            sortKey='sortValue'
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Catégorie'
                            values={category}
                            options={CATEGORIES_LABELS.map(c => ({ id: c, value: c, label: c }))}
                            onChange={v => onChange({ ...filter, category: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Rapporteur'
                            values={reporter}
                            options={sortBy(uniqBy(tickets, 'reporter').map(t => ({ id: t.reporter, value: t.handler, label: t.reporter })), 'value')}
                            onChange={v => onChange({ ...filter, reporter: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Assigné à'
                            values={handler}
                            options={sortBy(uniqBy(tickets, 'handler').map(t => ({ id: t.handler, value: t.handler, label: t.handler })), 'value')}
                            onChange={v => onChange({ ...filter, handler: v })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            multiple
                            label='Projet'
                            values={project}
                            options={sortBy(uniqBy(tickets, 'project').map(t => ({ id: t.project, value: t.project, label: t.project })), 'value')}
                            onChange={v => onChange({ ...filter, project: v })}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select label='Source' items={SOURCE_OPTIONS} value={source} onChange={onChangeSource} sortItems={false} />
                    </Grid>
                    <Grid size={4} container alignItems='center' spacing={1}>
                        <Grid size='grow'>
                            <Select label='Vue' items={VIEW_OPTIONS} value={viewMode} onChange={onChangeViewMode} sortItems={false} />
                        </Grid>
                        <Button variant='outlined' onClick={() => onChange({})}>Réinitialiser</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterCard.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    filter: PropTypes.shape({}),
    onChange: PropTypes.func,
    viewMode: PropTypes.string,
    onChangeViewMode: PropTypes.func,
    source: PropTypes.string,
    onChangeSource: PropTypes.func,
}

const DevsApp = () => {
    const [filter, setFilter] = useLocalStorage('filterDev', {})
    const [viewMode, setViewMode] = useLocalStorage('devViewMode', VIEW_LOT)
    const [source, setSource] = useLocalStorage('devLotsSource', SOURCE_MANTIS)
    const [openCreate, setOpenCreate] = useState(false)
    const [openSync, setOpenSync] = useState(false)
    const [lightMode, setLightMode] = useState(false)
    const [searchParams] = useSearchParams()

    useTitles(() => [{
        label: 'Suivi des lots',
        href: '/devs',
    }], [])

    // Arrivée depuis un lien externe : `/devs?lot=...` applique le filtre lot (valeur exacte),
    // `/devs?search=...` remplit le champ de recherche (nom de lot partiel, ex. depuis les Priorités).
    useEffect(() => {
        const lotParam = searchParams.get('lot')
        const searchParam = searchParams.get('search')
        if (lotParam) {
            setFilter({ lot: [lotParam] })
        } else if (searchParam) {
            setFilter({ searchValue: searchParam })
        }
    }, [])

    const isAquaparc = source === SOURCE_AQUAPARC

    const {
        data: mantisTickets = [],
        reload: reloadTickets,
        status: ticketsState,
    } = useFetch(ApplicationConf.support.statisticsCreated(), { method: 'POST', body: {} })

    const {
        data: devLots,
        reload: reloadDevLots,
        status: devLotsState,
    } = useFetch(isAquaparc ? ApplicationConf.devLot.all() : undefined)

    // Même source que l'écran Recette : tickets en « Attente validation interne » = à venir pour la prochaine recette.
    const {
        data: attenteValidation = {},
        reload: reloadAttenteValidation,
    } = useFetch(ApplicationConf.support.attenteValidationCount())

    const reload = () => {
        reloadTickets()
        reloadAttenteValidation()
        if (isAquaparc) {
            reloadDevLots()
        }
    }
    const state = (ticketsState === STATUS.LOADING || devLotsState === STATUS.LOADING) ? STATUS.LOADING : ticketsState

    const tickets = useMemo(() => {
        if (!isAquaparc) {
            return withMantisLots(mantisTickets)
        }
        return isNil(devLots) ? [] : withDevLots(mantisTickets, devLots)
    }, [isAquaparc, mantisTickets, devLots])

    const changeSource = (newSource) => {
        setSource(newSource)
        setFilter(prevState => ({ ...prevState, lot: [] }))
    }

    useActions(() => [{
        type: REFRESH,
        onClick: reload,
        loading: state === STATUS.LOADING,
    }, {
        type: OTHER,
        icon: lightMode ? 'display_settings' : 'airplay',
        tooltip: lightMode ? 'Mode édition' : 'Mode allégé',
        onClick: () => setLightMode((prevState) => !prevState),
    }, {
        type: OTHER,
        icon: 'flaky',
        tooltip: 'Afficher / masquer les tickets finalisés',
        onClick: () => setFilter((prevState) => ({
            ...prevState,
            status: prevState.status?.length ? [] : [
                STATUS_TICKETS_ID_AND_LABEL.NOUVEAU,
                STATUS_TICKETS_ID_AND_LABEL.AFFECTE,
                STATUS_TICKETS_ID_AND_LABEL.COMMENTAIRE,
                STATUS_TICKETS_ID_AND_LABEL.EN_COURS,
                STATUS_TICKETS_ID_AND_LABEL.REOUVERT,
            ],
        })),
    }, ...(isAquaparc ? [{
        type: OTHER,
        icon: 'sync',
        tooltip: 'Synchroniser les lots depuis Mantis',
        onClick: () => setOpenSync(true),
    }] : []), {
        type: NEW,
        tooltip: 'Créer un lot',
        onClick: () => setOpenCreate(true),
    }], [lightMode, state, isAquaparc])

    const ticketsFiltered = useMemo(() => {
        const { searchValue, lot, status, category, reporter, handler, project } = filter
        const searchValueFiltered = searchValue ? tickets.filter(t => normalizeString(`${t.summary} ${t.lot}`).includes(normalizeString(searchValue))) : tickets
        const lotFiltered = lot?.length ? searchValueFiltered.filter(t => lot.includes(t.lot)) : searchValueFiltered
        const reporterFiltered = reporter?.length ? lotFiltered.filter(t => reporter.includes(t.reporter)) : lotFiltered
        const handlerFiltered = handler?.length ? reporterFiltered.filter(t => handler.includes(t.handler)) : reporterFiltered
        const statusFiltered = status?.length ? handlerFiltered.filter(t => status.some(s => STATUS_TICKETS_ID_AND_LABEL[s] === t.status)) : handlerFiltered
        const categoryFiltered = category?.length ? statusFiltered.filter(t => category.includes(t.category)) : statusFiltered
        const projectFiltered = project?.length ? categoryFiltered.filter(t => project.includes(t.project)) : categoryFiltered
        return projectFiltered
    }, [tickets, filter])

    const {
        [LOT_GROUP.CURRENTS]: currentsTickets = [],
        [LOT_GROUP.TO_COME]: toComeTickets = [],
        [LOT_GROUP.CLOSED]: closedTickets = [],
        [LOT_GROUP.TO_ARBITRATE]: toArbitrateTickets = [],
        [LOT_GROUP.SUPPORT]: supportTickets = [],
        [LOT_GROUP.LOST]: lostTickets = [], // :shrug:
    } = useMemo(() => groupBy(ticketsFiltered, 'lotGroup'), [ticketsFiltered])

    const createLot = (lot) => DevLotsAction.create(lot).then(response => {
        if (!isNil(response)) {
            setOpenCreate(false)
            reload()
        }
    })

    const visibleHeaders = useMemo(() => getVisibleHeaders(ticketsFiltered), [ticketsFiltered])
    const recetteTickets = useMemo(() => ticketsFiltered.filter(t => t.version === 'En recette'), [ticketsFiltered])
    const sectionProps = { reload, lightMode, source, allTickets: tickets, headers: visibleHeaders }

    return (
        <Stack spacing={2}>
            <Grid container spacing={1}>
                <Grid size={8}>
                    <FilterCard tickets={tickets} filter={filter} onChange={setFilter} viewMode={viewMode} onChangeViewMode={setViewMode} source={source} onChangeSource={changeSource} />
                </Grid>
                <Grid size={4}>
                    <DevStatsCard currentsTickets={currentsTickets} toComeTickets={toComeTickets} closedTickets={closedTickets} recetteTickets={recetteTickets} nbToCome={attenteValidation.count} />
                </Grid>
            </Grid>
            {isAquaparc && devLotsState === STATUS.REJECTED && (
                <Typography color='error'>Impossible de charger les lots Aquaparc (service ou tables non déployés ?). Repassez sur les lots Mantis.</Typography>
            )}
            {viewMode === VIEW_LOT && (
                <>
                    {!!currentsTickets?.length && <AccordionLots title='Lots en cours' tickets={currentsTickets} defaultExpanded accentColor={PASTEL_COLOR.orange} {...sectionProps} />}
                    {!!toComeTickets?.length && <AccordionLots title='Lots à venir' tickets={toComeTickets} accentColor={PASTEL_COLOR.red} {...sectionProps} />}
                    {!!supportTickets?.length && <AccordionLots title='Lots du support' tickets={supportTickets} accentColor={mainBlue} {...sectionProps} />}
                    {!!toArbitrateTickets?.length && <AccordionLots title={'Lots d\'arbitrage'} tickets={toArbitrateTickets} accentColor='#9b59b6' {...sectionProps} />}
                    {!!closedTickets?.length && <AccordionLots title='Lots fermés' tickets={closedTickets} accentColor={PASTEL_COLOR.green} {...sectionProps} />}
                    {!!lostTickets?.length && <AccordionLots title={isAquaparc ? 'Non affectés' : 'Autres'} tickets={lostTickets} {...sectionProps} />}
                </>
            )}
            {viewMode === VIEW_PROJECT && <ProjectsView tickets={ticketsFiltered} />}
            {viewMode === VIEW_AFFECTATION && <AffectationView tickets={ticketsFiltered} />}
            {!isAquaparc && (
                <DialogCreateLot
                    open={openCreate}
                    onClose={() => setOpenCreate(false)}
                    onValidate={(lotName, ticketsToAdd) => {
                        IssuesAction.updateIssues(ticketsToAdd.split(',').map(id => ({ id: Number(id.replace(' ', '')), lot: lotName }))).then(() => reload())
                    }}
                />
            )}
            {isAquaparc && openSync && !isNil(devLots) && (
                <DialogSyncLots
                    mantisTickets={mantisTickets}
                    devLots={devLots}
                    onClose={() => setOpenSync(false)}
                    onDone={() => {
                        setOpenSync(false)
                        reload()
                    }}
                />
            )}
            {isAquaparc && openCreate && <DialogLotDev allTickets={tickets} onClose={() => setOpenCreate(false)} onValidate={createLot} />}
        </Stack>
    )
}

export default DevsApp